import React, { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Row,
  Card,
  CardHeader,
  CardBody,
  Button,
  InputGroup,
  InputGroupAddon,
  Input,
  InputGroupText
} from 'reactstrap';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import ModalConfirmation from 'components/Modal/ConfirmationModal';
import { useHistory } from 'react-router-dom';
import ReactLoader from 'components/Loader';
import useVoiceChannels from 'hooks/useVoiceChannels';
import { FlexTableV2 } from 'components/FlexTableV2';

const MonitoringVoiceChannelList = () => {
  const history = useHistory();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [columnOrder, setColumnOrder] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnSizing, setColumnSizing] = useState({});

  const [idResource, setIdResource] = useState();
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);
  const [searchText, setSearchText] = useState('');

  const {
    response: channels,
    totalRegisters,
    isLoading,
    fetch,
    deleteChannel
  } = useVoiceChannels({ itemsPerPage: pagination.pageSize });

  useEffect(() => {
    fetch(pagination.pageIndex + 1, searchText);
  }, [pagination.pageIndex, pagination.pageSize, searchText, fetch]);

  const onSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleUpdate = (id) => {
    history.push(`/admin/voice_channels/form/${id}`);
  };

  const controlDeleteResource = (id) => {
    setIdResource(id);
    setOpenModalConfirmation(true);
  };

  const confirmDelete = async (id) => {
    const success = await deleteChannel(id);
    if (success) {
      setOpenModalConfirmation(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        header: 'Nome do Canal',
        accessorKey: 'name',
        size: 250
      },
      {
        header: 'Descrição',
        accessorKey: 'description',
        size: 350,
        cell: ({ getValue }) => {
          const value = getValue();
          return (
            <div
              title={value}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '300px',
                display: 'block'
              }}
            >
              {value || '-'}
            </div>
          );
        }
      },
      {
        id: 'actions',
        header: 'Ações',
        size: 100,
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              size="sm"
              color="info"
              onClick={() => handleUpdate(row.original.id)}
              className="mr-2"
              title="Editar"
            >
              <li className="fas fa-edit"></li>
            </Button>
            <Button
              size="sm"
              color="danger"
              onClick={() => controlDeleteResource(row.original.id)}
              title="Excluir"
            >
              <li className="fas fa-trash"></li>
            </Button>
          </div>
        )
      }
    ],

    []
  );

  return (
    <>
      <SimpleHeader
        setCreate="/admin/voice_channels/form"
        name="Canais de Voz"
        parentName="Monitoramento"
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">Canais Cadastrados</h3>
                </CardHeader>
                <CardBody>
                  <Row className="mb-4">
                    <div className="col-md-12">
                      <InputGroup>
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="fas fa-search" />
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder="Buscar por nome..."
                          type="text"
                          value={searchText}
                          onChange={onSearch}
                        />
                      </InputGroup>
                    </div>
                  </Row>

                  {isLoading ? (
                    <Row className="justify-content-md-center py-4">
                      <ReactLoader />
                    </Row>
                  ) : (
                    <FlexTableV2
                      state={{
                        pagination,
                        setPagination,
                        columnOrder,
                        setColumnOrder,
                        columnVisibility,
                        setColumnVisibility,
                        columnSizing,
                        setColumnSizing
                      }}
                      table={{
                        data: channels,
                        columns,
                        rowCount: totalRegisters,
                        manualPagination: true,
                        enableLocalStorageMemo: true,
                        enableLocalStorageClearing: true,
                        localStorageKey: 'monitoringVoiceChannelsList',
                        scrollContainerHeight: 'unset'
                      }}
                    />
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>

        <ModalConfirmation
          isModalOpen={openModalConfirmation}
          onModalToggle={setOpenModalConfirmation}
          unmountOnClose={true}
          idResource={idResource}
          deleteResource={confirmDelete}
          message={'Tem certeza que deseja excluir este Canal de Voz?'}
        />
      </Container>
    </>
  );
};

export default MonitoringVoiceChannelList;
